import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, Calendar, Clock } from 'lucide-react';
import api from '../api/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

export const BookingConfirm = () => {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading, success, already_confirmed, error
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    confirmBooking();
  }, [token]);

  const confirmBooking = async () => {
    try {
      const response = await api.get(`/bookings/confirm/${token}`);
      setBooking(response.data.booking);
      
      if (response.data.already_confirmed) {
        setStatus('already_confirmed');
      } else {
        setStatus('success');
      }
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.detail || 'Error al confirmar la reserva');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-AR', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="min-h-screen py-20 px-6 flex items-center justify-center" data-testid="booking-confirm-page">
      <Card className="w-full max-w-md glass-effect border-white/10">
        <CardHeader className="text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="h-16 w-16 mx-auto animate-spin text-primary" />
              <CardTitle className="text-2xl font-heading uppercase mt-4">Confirmando...</CardTitle>
            </>
          )}
          
          {status === 'success' && (
            <>
              <CheckCircle className="h-16 w-16 mx-auto text-green-500" />
              <CardTitle className="text-2xl font-heading uppercase mt-4 text-green-500">¡Reserva Confirmada!</CardTitle>
            </>
          )}
          
          {status === 'already_confirmed' && (
            <>
              <CheckCircle className="h-16 w-16 mx-auto text-blue-500" />
              <CardTitle className="text-2xl font-heading uppercase mt-4 text-blue-500">Ya Confirmada</CardTitle>
            </>
          )}
          
          {status === 'error' && (
            <>
              <XCircle className="h-16 w-16 mx-auto text-red-500" />
              <CardTitle className="text-2xl font-heading uppercase mt-4 text-red-500">Error</CardTitle>
            </>
          )}
        </CardHeader>
        
        <CardContent className="text-center">
          {status === 'success' && booking && (
            <div className="space-y-4">
              <p className="text-muted-foreground">Tu turno ha sido confirmado exitosamente. ¡Te esperamos!</p>
              
              <div className="bg-primary/10 border border-primary/30 rounded-lg p-4 mt-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Calendar className="h-5 w-5 text-primary" />
                  <span className="font-semibold">{formatDate(booking.date)}</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <Clock className="h-5 w-5 text-primary" />
                  <span className="font-semibold">{booking.time_slot} hs</span>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground mt-4">
                Te enviamos un email con los detalles de tu reserva.
              </p>
            </div>
          )}
          
          {status === 'already_confirmed' && booking && (
            <div className="space-y-4">
              <p className="text-muted-foreground">Esta reserva ya había sido confirmada anteriormente.</p>
              
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mt-4">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Calendar className="h-5 w-5 text-blue-500" />
                  <span className="font-semibold">{formatDate(booking.date)}</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <Clock className="h-5 w-5 text-blue-500" />
                  <span className="font-semibold">{booking.time_slot} hs</span>
                </div>
              </div>
            </div>
          )}
          
          {status === 'error' && (
            <p className="text-muted-foreground">{error}</p>
          )}
          
          <div className="mt-6 space-y-3">
            <Link to="/dashboard">
              <Button className="w-full bg-primary hover:bg-primary/90 font-bold uppercase" data-testid="go-to-dashboard">
                Ir al Dashboard
              </Button>
            </Link>
            <Link to="/booking">
              <Button variant="outline" className="w-full border-white/20 hover:bg-white/5" data-testid="new-booking">
                Nueva Reserva
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
